import React from 'react';
import { TrendingUp, TrendingDown } from 'lucide-react';
import { TabSwitcher } from 'equal-ds-ui';
import type { TrafficSplitResponse, ConversionResponse } from '../../../types/smart-router';

interface Props {
  trafficSplit: TrafficSplitResponse;
  conversion: ConversionResponse;
  level: 'consent' | 'user';
  onLevelChange: (level: 'consent' | 'user') => void;
}

const fmt = (n: number) => n.toLocaleString('en-IN');
const rateColor = (rate: number) => rate >= 70 ? 'text-green-600' : rate >= 60 ? 'text-yellow-600' : 'text-red-600';

const Change: React.FC<{ value: number }> = ({ value }) => {
  const pos = value >= 0;
  return (
    <span className={`inline-flex items-center gap-1 text-xs mt-1 ${pos ? 'text-green-600' : 'text-red-600'}`}>
      {pos ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
      {Math.abs(value)}% vs last period
    </span>
  );
};

const ProgressBar: React.FC<{ pct: number; color: string }> = ({ pct, color }) => (
  <div className="w-full h-2 rounded-full mt-2 bg-background-secondary">
    <div className="h-2 rounded-full transition-all duration-700" style={{ width: `${Math.min(pct, 100)}%`, background: color }} />
  </div>
);

export const SmartRouterKpiCards: React.FC<Props> = ({ trafficSplit, conversion, level, onLevelChange }) => {
  const levelKey = level === 'consent' ? 'consent_level' : 'user_level';
  const withSr = conversion.with_sr[levelKey];
  const withoutSr = conversion.without_sr[levelKey];
  const overallTotal = (withSr?.total ?? 0) + (withoutSr?.total ?? 0);
  const overallSuccess = (withSr?.success ?? 0) + (withoutSr?.success ?? 0);
  const overallRate = overallTotal > 0 ? Math.round((overallSuccess / overallTotal) * 1000) / 10 : 0;
  const overallRequests = trafficSplit.with_sr.total_consents + trafficSplit.without_sr.total_consents;

  return (
    <div className="border border-border-default rounded-xl shadow-md overflow-hidden">
      {/* Header - matches equal-ds Table header gradient */}
      <div className="bg-gradient-to-b from-primary-50 to-primary-100 px-4 py-3 flex items-center justify-between">
        <h2 className="text-sm font-semibold text-text-primary tracking-wider">Overview</h2>
        <TabSwitcher
          tabs={[{ id: 'consent', label: 'Consent Level' }, { id: 'user', label: 'User Level' }]}
          activeTab={level}
          onTabChange={(tab) => onLevelChange(tab as 'consent' | 'user')}
          variant="horizontal"
        />
      </div>

      {/* Body */}
      <div className="bg-white p-5">
        {/* Column Headers */}
        <div className="grid grid-cols-3 gap-6 mb-5">
          <div className="text-center"><span className="text-xs font-normal text-text-tertiary uppercase tracking-wide">Overall</span></div>
          <div className="text-center"><span className="text-xs font-normal text-primary-600 uppercase tracking-wide">With SR</span></div>
          <div className="text-center"><span className="text-xs font-normal text-yellow-600 uppercase tracking-wide">Without SR</span></div>
        </div>

        {/* Total Requests */}
        <div className="grid grid-cols-3 gap-6 mb-1">
          <div className="text-center">
            <p className="text-xs text-text-tertiary mb-1">Total Requests</p>
            <p className="text-2xl font-semibold text-text-primary">{fmt(overallRequests)}</p>
          </div>
          <div className="text-center">
            <p className="text-xs text-text-tertiary mb-1">Total Requests</p>
            <p className="text-2xl font-semibold text-text-primary">{fmt(trafficSplit.with_sr.total_consents)}</p>
            <Change value={trafficSplit.with_sr.change_percentage} />
          </div>
          <div className="text-center">
            <p className="text-xs text-text-tertiary mb-1">Total Requests</p>
            <p className="text-2xl font-semibold text-text-primary">{fmt(trafficSplit.without_sr.total_consents)}</p>
            <Change value={trafficSplit.without_sr.change_percentage} />
          </div>
        </div>

        <div className="my-4 border-t border-border-default" />

        {/* Traffic Share */}
        <div className="grid grid-cols-3 gap-6 mb-1">
          <div className="text-center">
            <p className="text-xs text-text-tertiary mb-1">Traffic Share</p>
            <p className="text-lg font-medium text-text-primary">100%</p>
            <ProgressBar pct={100} color="#9ca3af" />
          </div>
          <div className="text-center">
            <p className="text-xs text-text-tertiary mb-1">Traffic Share</p>
            <p className="text-lg font-medium text-text-primary">{trafficSplit.with_sr.percentage}%</p>
            <ProgressBar pct={trafficSplit.with_sr.percentage} color="#6366f1" />
          </div>
          <div className="text-center">
            <p className="text-xs text-text-tertiary mb-1">Traffic Share</p>
            <p className="text-lg font-medium text-text-primary">{trafficSplit.without_sr.percentage}%</p>
            <ProgressBar pct={trafficSplit.without_sr.percentage} color="#f59e0b" />
          </div>
        </div>

        <div className="my-4 border-t border-border-default" />

        {/* Conversion Rate */}
        <div className="grid grid-cols-3 gap-6">
          <div className="text-center">
            <p className="text-xs text-text-tertiary mb-1">Conversion Rate</p>
            <p className={`text-xl font-semibold ${rateColor(overallRate)}`}>{overallRate}%</p>
            <p className="text-xs text-text-tertiary mt-1">{fmt(overallSuccess)} / {fmt(overallTotal)}</p>
          </div>
          <div className="text-center">
            <p className="text-xs text-text-tertiary mb-1">Conversion Rate</p>
            <p className={`text-xl font-semibold ${rateColor(withSr?.success_rate ?? 0)}`}>{withSr?.success_rate ?? 0}%</p>
            <p className="text-xs text-text-tertiary mt-1">{fmt(withSr?.success ?? 0)} / {fmt(withSr?.total ?? 0)}</p>
          </div>
          <div className="text-center">
            <p className="text-xs text-text-tertiary mb-1">Conversion Rate</p>
            <p className={`text-xl font-semibold ${rateColor(withoutSr?.success_rate ?? 0)}`}>{withoutSr?.success_rate ?? 0}%</p>
            <p className="text-xs text-text-tertiary mt-1">{fmt(withoutSr?.success ?? 0)} / {fmt(withoutSr?.total ?? 0)}</p>
          </div>
        </div>
      </div>
    </div>
  );
};
